/**
 * Locale-aware copy for graph collection surfaces (legacy CRA app).
 * Mirrors the Search/searchCopy.ts pattern (en-US, ja-JP, ko-KR).
 *
 * `formatGraphMessage` performs basic token substitution AND ICU-plural-style
 * fallback for keys shaped like `{count, plural, one {...} other {...}}` so
 * localized count phrases can avoid string concatenation (per rule 13).
 */
export type GraphLocale = "en-US" | "ja-JP" | "ko-KR";

type GraphCopyKey =
  | "feedEmptyTitle"
  | "feedEmptySubtitle"
  | "feedLoading"
  | "createYourOwn"
  | "createTitle"
  | "connectGraphs"
  | "extendTitle"
  | "rebrewCopy"
  | "rebrewSync"
  | "rebrewSubmit"
  | "rebrewSourceLabel"
  | "rebrewSourceInvalid"
  | "rebrewError"
  | "blankSearchHint"
  | "blankDraftHint"
  | "publishGraph"
  | "startFromIssue"
  | "startFromThisGraph"
  | "remixTheseGraphs"
  | "seedAndRemix"
  | "draftBadge"
  | "openGraph"
  | "openFullscreenGraphAria"
  | "draftGraphCreatedToast"
  | "untitledGraph"
  | "unknownIssue"
  | "addIssueSearchPlaceholder"
  | "addIssueToGraph"
  | "issueAddedToGraph"
  | "issueAlreadyInGraph"
  | "graphTitleLabel"
  | "detailsButton"
  | "graphDetailsTitle"
  | "titleSaveError"
  | "saveChangesTitle"
  | "saveChangesDescription"
  | "saveChangesConfirm"
  | "discardChanges"
  | "stickyCommentHint"
  | "edgeNoteSubmit"
  | "dismissComposer"
  | "edgeInspectorTitle"
  | "edgeInspectorEmpty"
  | "edgeInspectorTimeline"
  | "edgeInspectorPlace"
  | "closeEdgeInspector"
  | "edgeNewBadge"
  | "edgeNoteSavedToast"
  | "edgeNoteSaveError"
  | "edgeNoteDeletedToast"
  | "edgeNoteDeleteError"
  | "editNote"
  | "deleteNote"
  | "addNotePlaceholder"
  | "addFirstNotePlaceholder"
  | "noteDeleteConfirmation"
  | "publishError"
  | "influencersLabel"
  | "influencedLabel"
  | "loadMoreConnections"
  | "dragGroupAria"
  | "dragGroupTitle"
  | "addInfluencer"
  | "addInfluenced"
  | "zoomOut"
  | "zoomIn"
  | "recenter"
  | "zoomToFit"
  | "zoomToContent"
  | "decreaseDepth"
  | "increaseDepth"
  | "depthAria"
  | "addInfluenceTitleInfluencer"
  | "addInfluenceTitleInfluenced"
  | "searchInfluencers"
  | "searchInfluenced"
  | "selectIssueFirst"
  | "influenceAddedToast"
  | "influenceConnectedToast"
  | "influenceConnectError"
  | "influenceAlreadyVisibleToast"
  | "influenceCreateError"
  | "addInfluenceHintInfluencer"
  | "addInfluenceHintInfluenced"
  | "loadingGraph"
  | "exploreTab"
  | "feedTab"
  | "editorTab"
  | "collectionCreatedToast"
  | "loadGraphError"
  | "positionSaveError"
  | "seedAttachError"
  | "graphCollectionTitle"
  | "details"
  | "delete"
  | "saveDraft"
  | "publish"
  | "save"
  | "deleteConfirmationTitle"
  | "deleteConfirmationBody"
  | "draftSavedToast"
  | "publishedToast"
  | "titleRequiredForPublish"
  | "undo"
  | "removeIssueCta"
  | "removeInfluenceCta"
  | "removeIssueToast"
  | "removeInfluenceToast"
  | "removeIssueError"
  | "removeInfluenceError"
  | "restoreSucceededToast"
  | "restoreFailedToast"
  | "otherCollectionsBadge"
  | "notesBadge"
  | "impactsBadge"
  | "solutionsBadge"
  | "actionsBadge"
  | "sourceSelfLabel"
  | "sourceLinkedLabel"
  | "sourceAnonymousActor"
  | "remixHeader"
  | "remixSourcesSelected"
  | "remixBridgeHint"
  | "remixSharedNodeHint"
  | "remixSourceChipAria"
  | "extendPanelTitle"
  | "extendPanelSubtitle"
  | "extendSearchPlaceholder"
  | "extendClearSearch"
  | "extendSearchMinLength"
  | "extendNoResults"
  | "extendLoadMore"
  | "extendAddToSelection"
  | "extendRemoveFromSelection"
  | "selectionClearedHint"
  | "focusEdgeHint"
  | "neighborhoodHint"
  | "liveRegionNeighborhood"
  | "liveRegionFocus"
  | "liveRegionCleared"
  | "feedEmptyHint";

const GRAPH_COPY: Record<GraphLocale, Record<GraphCopyKey, string>> = {
  "en-US": {
    feedEmptyTitle: "No graph collections yet",
    feedEmptySubtitle: "Published influence graphs will show up here.",
    feedEmptyHint: "Tap Create to start your first graph, or Extend an existing one.",
    feedLoading: "Loading graph collections…",
    createYourOwn: "Create",
    createTitle: "Create",
    connectGraphs: "Extend an existing graph",
    extendTitle: "Extend an existing graph",
    rebrewCopy: "Copy items",
    rebrewSync: "Stay linked",
    rebrewSubmit: "Start from this graph",
    rebrewSourceLabel: "Source graph",
    rebrewSourceInvalid: "Select a valid source graph.",
    rebrewError: "Couldn't start from that graph right now.",
    blankSearchHint: "Search for an issue to start your graph",
    blankDraftHint: "Start a new graph by picking an issue or extend an existing graph below.",
    publishGraph: "Publish graph",
    startFromIssue: "Start from this issue",
    startFromThisGraph: "Start from this graph",
    remixTheseGraphs: "Remix these graphs",
    seedAndRemix: "Seed and remix",
    draftBadge: "Draft",
    openGraph: "Open graph",
    openFullscreenGraphAria: "Open the {title} graph in fullscreen",
    draftGraphCreatedToast: "Graph draft started",
    untitledGraph: "Untitled graph",
    unknownIssue: "Unknown issue",
    addIssueSearchPlaceholder: "Add another issue to this graph",
    addIssueToGraph: "Add to graph",
    issueAddedToGraph: "Issue added to the graph",
    issueAlreadyInGraph: "That issue is already on this graph",
    graphTitleLabel: "title",
    detailsButton: "Details",
    graphDetailsTitle: "Graph details",
    titleSaveError: "Couldn't save the graph title.",
    saveChangesTitle: "Save changes?",
    saveChangesDescription: "Save the graph details you changed before closing?",
    saveChangesConfirm: "Yes",
    discardChanges: "No",
    stickyCommentHint: "Add context on this edge (optional timeline & place)",
    edgeNoteSubmit: "Save edge note",
    dismissComposer: "Not now",
    edgeInspectorTitle: "Influence edge",
    edgeInspectorEmpty: "No notes on this edge yet.",
    edgeInspectorTimeline: "When",
    edgeInspectorPlace: "Where",
    closeEdgeInspector: "Close edge details",
    edgeNewBadge: "New",
    edgeNoteSavedToast: "Edge note saved",
    edgeNoteSaveError: "Couldn't save the edge note.",
    edgeNoteDeletedToast: "Note removed",
    edgeNoteDeleteError: "Couldn't remove the note.",
    editNote: "Edit",
    deleteNote: "Delete",
    addNotePlaceholder: "Add a note to this edge…",
    addFirstNotePlaceholder: "Leave the first note on this edge…",
    noteDeleteConfirmation: "Remove this note?",
    publishError: "Couldn't publish this graph right now.",
    influencersLabel: "influencers",
    influencedLabel: "influenced",
    loadMoreConnections: "load {count} more",
    dragGroupAria: "Drag or nudge this connected group",
    dragGroupTitle: "Drag to move the connected group",
    addInfluencer: "Add influencer",
    addInfluenced: "Add influenced",
    zoomOut: "Zoom out",
    zoomIn: "Zoom in",
    recenter: "Recenter",
    zoomToFit: "Zoom to fit",
    zoomToContent: "Zoom to content",
    decreaseDepth: "Decrease depth",
    increaseDepth: "Increase depth",
    depthAria: "Depth {depth}",
    addInfluenceTitleInfluencer: "Add Influencer",
    addInfluenceTitleInfluenced: "Add Influenced",
    searchInfluencers: "Search influencers",
    searchInfluenced: "Search influenced",
    selectIssueFirst: "Please select an issue first",
    influenceAddedToast: "Influence relationship added successfully",
    influenceConnectedToast: "Edge created",
    influenceConnectError: "Couldn't create that connection right now.",
    influenceAlreadyVisibleToast: "Influence already exists. Added the issue to this graph.",
    influenceCreateError: "Couldn't create the influence relationship right now.",
    addInfluenceHintInfluencer: "Add an issue that influences \"{issueName}\".",
    addInfluenceHintInfluenced: "Add an issue that is influenced by \"{issueName}\".",
    loadingGraph: "Loading graph…",
    exploreTab: "Explore",
    feedTab: "Feed",
    editorTab: "Editor",
    collectionCreatedToast: "Graph collection published",
    loadGraphError: "Unable to load this graph collection right now.",
    positionSaveError: "Couldn't save the latest graph positions.",
    seedAttachError: "The graph was created, but the seed issue couldn't be attached.",
    graphCollectionTitle: "Graph Collection",
    details: "Details",
    delete: "Delete",
    saveDraft: "Save Draft",
    publish: "Publish",
    save: "Save",
    deleteConfirmationTitle: "Delete graph collection?",
    deleteConfirmationBody: "This action cannot be undone. The graph and all its connections will be permanently removed.",
    draftSavedToast: "Draft saved successfully",
    publishedToast: "Graph published successfully",
    titleRequiredForPublish: "Add a title before publishing.",
    undo: "Undo",
    removeIssueCta: "Remove from this graph",
    removeInfluenceCta: "Remove influence from this graph",
    removeIssueToast: "Issue removed from this graph",
    removeInfluenceToast: "Influence removed from this graph",
    removeIssueError: "Couldn't remove this issue right now.",
    removeInfluenceError: "Couldn't remove this influence right now.",
    restoreSucceededToast: "Restored",
    restoreFailedToast: "Couldn't restore that change.",
    otherCollectionsBadge: "{count, plural, one {# other collection uses this link} other {# other collections use this link}}",
    notesBadge: "{count, plural, one {# note on this edge} other {# notes on this edge}}",
    impactsBadge: "{count, plural, one {# impact tied to this link} other {# impacts tied to this link}}",
    solutionsBadge: "{count, plural, one {# solution tied to this link} other {# solutions tied to this link}}",
    actionsBadge: "{count, plural, one {# action tied to this link} other {# actions tied to this link}}",
    sourceSelfLabel: "This graph",
    sourceLinkedLabel: "From {actor}",
    sourceAnonymousActor: "anonymous",
    remixHeader: "Remix",
    remixSourcesSelected: "{count, plural, one {1 source selected} other {# sources selected}}",
    remixBridgeHint: "Drag between any two nodes to bridge the sources.",
    remixSharedNodeHint: "Shared across {count} sources",
    remixSourceChipAria: "Source graph {title} by {actor}",
    extendPanelTitle: "Extend an existing graph",
    extendPanelSubtitle: "Pick one to start from; pick two or more to remix.",
    extendSearchPlaceholder: "Search public graph collections",
    extendClearSearch: "Clear graph collection search",
    extendSearchMinLength: "Type at least 2 characters.",
    extendNoResults: "No public graphs match that search.",
    extendLoadMore: "Load more",
    extendAddToSelection: "Add to selection",
    extendRemoveFromSelection: "Remove from selection",
    selectionClearedHint: "Selection cleared",
    focusEdgeHint: "Focused on one edge",
    neighborhoodHint: "{count, plural, one {# connected edge shown} other {# connected edges shown}}",
    liveRegionNeighborhood: "{count, plural, one {Showing # related edge.} other {Showing # related edges.}}",
    liveRegionFocus: "Focused on one edge.",
    liveRegionCleared: "Selection cleared.",
  },
  "ja-JP": {
    feedEmptyTitle: "グラフコレクションはまだありません",
    feedEmptySubtitle: "公開された影響グラフがここに表示されます。",
    feedEmptyHint: "「作成」で最初のグラフを始めるか、既存のグラフを拡張しましょう。",
    feedLoading: "グラフコレクションを読み込み中…",
    createYourOwn: "作成",
    createTitle: "作成",
    connectGraphs: "既存のグラフを拡張",
    extendTitle: "既存のグラフを拡張",
    rebrewCopy: "項目をコピー",
    rebrewSync: "リンクを維持",
    rebrewSubmit: "このグラフから始める",
    rebrewSourceLabel: "元のグラフ",
    rebrewSourceInvalid: "有効な元グラフを選んでください。",
    rebrewError: "今はそのグラフから開始できません。",
    blankSearchHint: "課題を検索してグラフを始めましょう",
    blankDraftHint: "課題を選ぶか、下から既存のグラフを拡張して新しいグラフを始めましょう。",
    publishGraph: "グラフを公開",
    startFromIssue: "この課題から始める",
    startFromThisGraph: "このグラフから始める",
    remixTheseGraphs: "これらのグラフをリミックス",
    seedAndRemix: "種にしてリミックス",
    draftBadge: "下書き",
    openGraph: "グラフを開く",
    openFullscreenGraphAria: "{title} グラフを全画面で開く",
    draftGraphCreatedToast: "グラフの下書きを開始しました",
    untitledGraph: "無題のグラフ",
    unknownIssue: "不明な課題",
    addIssueSearchPlaceholder: "このグラフに別の課題を追加",
    addIssueToGraph: "グラフに追加",
    issueAddedToGraph: "課題をグラフに追加しました",
    issueAlreadyInGraph: "その課題はすでにこのグラフにあります",
    graphTitleLabel: "タイトル",
    detailsButton: "詳細",
    graphDetailsTitle: "グラフの詳細",
    titleSaveError: "グラフのタイトルを保存できませんでした。",
    saveChangesTitle: "変更を保存しますか？",
    saveChangesDescription: "閉じる前に変更したグラフ詳細を保存しますか？",
    saveChangesConfirm: "はい",
    discardChanges: "いいえ",
    stickyCommentHint: "このエッジにコンテキストを追加（任意のタイムラインと場所）",
    edgeNoteSubmit: "エッジメモを保存",
    dismissComposer: "今はしない",
    edgeInspectorTitle: "影響エッジ",
    edgeInspectorEmpty: "このエッジにはまだメモがありません。",
    edgeInspectorTimeline: "時点",
    edgeInspectorPlace: "場所",
    closeEdgeInspector: "エッジ詳細を閉じる",
    edgeNewBadge: "新規",
    edgeNoteSavedToast: "エッジメモを保存しました",
    edgeNoteSaveError: "エッジメモを保存できませんでした。",
    edgeNoteDeletedToast: "メモを削除しました",
    edgeNoteDeleteError: "メモを削除できませんでした。",
    editNote: "編集",
    deleteNote: "削除",
    addNotePlaceholder: "このエッジにメモを追加…",
    addFirstNotePlaceholder: "このエッジに最初のメモを残しましょう…",
    noteDeleteConfirmation: "このメモを削除しますか？",
    publishError: "今はこのグラフを公開できません。",
    influencersLabel: "影響元",
    influencedLabel: "影響先",
    loadMoreConnections: "さらに{count}件読み込む",
    dragGroupAria: "この接続グループをドラッグまたは移動",
    dragGroupTitle: "ドラッグして接続グループを移動",
    addInfluencer: "影響元を追加",
    addInfluenced: "影響先を追加",
    zoomOut: "縮小",
    zoomIn: "拡大",
    recenter: "中央へ戻す",
    zoomToFit: "全体を表示",
    zoomToContent: "内容に合わせる",
    decreaseDepth: "深さを下げる",
    increaseDepth: "深さを上げる",
    depthAria: "深さ {depth}",
    addInfluenceTitleInfluencer: "影響元を追加",
    addInfluenceTitleInfluenced: "影響先を追加",
    searchInfluencers: "影響元を検索",
    searchInfluenced: "影響先を検索",
    selectIssueFirst: "先に課題を選択してください",
    influenceAddedToast: "影響関係を追加しました",
    influenceConnectedToast: "エッジを作成しました",
    influenceConnectError: "今はこの接続を作成できません。",
    influenceAlreadyVisibleToast: "影響関係は既にあります。このグラフに課題を追加しました。",
    influenceCreateError: "今は影響関係を作成できません。",
    addInfluenceHintInfluencer: "\"{issueName}\" に影響する課題を追加します。",
    addInfluenceHintInfluenced: "\"{issueName}\" から影響を受ける課題を追加します。",
    loadingGraph: "グラフを読み込み中…",
    exploreTab: "探索",
    feedTab: "フィード",
    editorTab: "編集",
    collectionCreatedToast: "グラフコレクションを公開しました",
    loadGraphError: "このグラフコレクションを今は読み込めません。",
    positionSaveError: "最新のグラフ位置を保存できませんでした。",
    seedAttachError: "グラフは作成されましたが、開始課題を追加できませんでした。",
    graphCollectionTitle: "グラフコレクション",
    details: "詳細",
    delete: "削除",
    saveDraft: "下書き保存",
    publish: "公開",
    save: "保存",
    deleteConfirmationTitle: "グラフコレクションを削除しますか？",
    deleteConfirmationBody: "この操作は元に戻せません。グラフとすべての接続が永久に削除されます。",
    draftSavedToast: "下書きを保存しました",
    publishedToast: "グラフを公開しました",
    titleRequiredForPublish: "公開する前にタイトルを追加してください。",
    undo: "元に戻す",
    removeIssueCta: "このグラフから外す",
    removeInfluenceCta: "このグラフから影響を外す",
    removeIssueToast: "課題をこのグラフから外しました",
    removeInfluenceToast: "影響をこのグラフから外しました",
    removeIssueError: "今は外せません。",
    removeInfluenceError: "今は影響を外せません。",
    restoreSucceededToast: "元に戻しました",
    restoreFailedToast: "元に戻せませんでした。",
    otherCollectionsBadge: "{count, plural, one {他に # 件のコレクションがこのリンクを利用中} other {他に # 件のコレクションがこのリンクを利用中}}",
    notesBadge: "{count, plural, one {このエッジに # 件のメモ} other {このエッジに # 件のメモ}}",
    impactsBadge: "{count, plural, one {このリンクに関連する # 件の影響} other {このリンクに関連する # 件の影響}}",
    solutionsBadge: "{count, plural, one {このリンクに関連する # 件の解決策} other {このリンクに関連する # 件の解決策}}",
    actionsBadge: "{count, plural, one {このリンクに関連する # 件のアクション} other {このリンクに関連する # 件のアクション}}",
    sourceSelfLabel: "このグラフ",
    sourceLinkedLabel: "{actor} より",
    sourceAnonymousActor: "匿名",
    remixHeader: "リミックス",
    remixSourcesSelected: "{count, plural, one {1 件のソースを選択中} other {# 件のソースを選択中}}",
    remixBridgeHint: "2つのノード間をドラッグしてソース間をブリッジ。",
    remixSharedNodeHint: "{count} 件のソースで共有",
    remixSourceChipAria: "ソースグラフ {title}（{actor}）",
    extendPanelTitle: "既存のグラフを拡張",
    extendPanelSubtitle: "1件なら元にして、2件以上ならリミックス。",
    extendSearchPlaceholder: "公開グラフコレクションを検索",
    extendClearSearch: "グラフコレクション検索をクリア",
    extendSearchMinLength: "2文字以上入力してください。",
    extendNoResults: "該当する公開グラフはありません。",
    extendLoadMore: "さらに読み込む",
    extendAddToSelection: "選択に追加",
    extendRemoveFromSelection: "選択から外す",
    selectionClearedHint: "選択を解除しました",
    focusEdgeHint: "1本のエッジに絞り込み",
    neighborhoodHint: "{count, plural, one {# 本の関連エッジを表示中} other {# 本の関連エッジを表示中}}",
    liveRegionNeighborhood: "{count, plural, one {関連エッジ # 本を表示中。} other {関連エッジ # 本を表示中。}}",
    liveRegionFocus: "1本のエッジに絞り込みました。",
    liveRegionCleared: "選択を解除しました。",
  },
  "ko-KR": {
    feedEmptyTitle: "아직 그래프 컬렉션이 없습니다",
    feedEmptySubtitle: "공개된 영향 그래프가 여기에 표시됩니다.",
    feedEmptyHint: "「만들기」로 첫 그래프를 시작하거나 기존 그래프를 확장하세요.",
    feedLoading: "그래프 컬렉션을 불러오는 중…",
    createYourOwn: "만들기",
    createTitle: "만들기",
    connectGraphs: "기존 그래프 확장",
    extendTitle: "기존 그래프 확장",
    rebrewCopy: "항목 복사",
    rebrewSync: "연결 유지",
    rebrewSubmit: "이 그래프로 시작",
    rebrewSourceLabel: "원본 그래프",
    rebrewSourceInvalid: "유효한 원본 그래프를 선택하세요.",
    rebrewError: "지금은 그 그래프에서 시작할 수 없습니다.",
    blankSearchHint: "이슈를 검색해 그래프를 시작하세요",
    blankDraftHint: "이슈를 고르거나 아래의 기존 그래프를 확장해 새로운 그래프를 시작하세요.",
    publishGraph: "그래프 게시",
    startFromIssue: "이 이슈에서 시작",
    startFromThisGraph: "이 그래프에서 시작",
    remixTheseGraphs: "이 그래프들을 리믹스",
    seedAndRemix: "씨앗으로 리믹스",
    draftBadge: "임시저장",
    openGraph: "그래프 열기",
    openFullscreenGraphAria: "{title} 그래프를 전체 화면으로 열기",
    draftGraphCreatedToast: "그래프 초안을 시작했습니다",
    untitledGraph: "제목 없는 그래프",
    unknownIssue: "알 수 없는 이슈",
    addIssueSearchPlaceholder: "이 그래프에 다른 이슈 추가",
    addIssueToGraph: "그래프에 추가",
    issueAddedToGraph: "이슈를 그래프에 추가했습니다",
    issueAlreadyInGraph: "해당 이슈는 이미 이 그래프에 있습니다",
    graphTitleLabel: "제목",
    detailsButton: "세부정보",
    graphDetailsTitle: "그래프 세부정보",
    titleSaveError: "그래프 제목을 저장할 수 없습니다.",
    saveChangesTitle: "변경사항을 저장할까요?",
    saveChangesDescription: "닫기 전에 변경한 그래프 세부정보를 저장할까요?",
    saveChangesConfirm: "예",
    discardChanges: "아니요",
    stickyCommentHint: "이 엣지에 맥락 추가(선택: 타임라인 및 장소)",
    edgeNoteSubmit: "엣지 메모 저장",
    dismissComposer: "나중에",
    edgeInspectorTitle: "영향 엣지",
    edgeInspectorEmpty: "이 엣지에는 아직 메모가 없습니다.",
    edgeInspectorTimeline: "시점",
    edgeInspectorPlace: "장소",
    closeEdgeInspector: "엣지 세부정보 닫기",
    edgeNewBadge: "새 항목",
    edgeNoteSavedToast: "엣지 메모를 저장했습니다",
    edgeNoteSaveError: "엣지 메모를 저장할 수 없습니다.",
    edgeNoteDeletedToast: "메모를 삭제했습니다",
    edgeNoteDeleteError: "메모를 삭제할 수 없습니다.",
    editNote: "수정",
    deleteNote: "삭제",
    addNotePlaceholder: "이 엣지에 메모 추가…",
    addFirstNotePlaceholder: "이 엣지에 첫 메모를 남겨보세요…",
    noteDeleteConfirmation: "이 메모를 삭제할까요?",
    publishError: "지금은 이 그래프를 게시할 수 없습니다.",
    influencersLabel: "영향 주는 이슈",
    influencedLabel: "영향받는 이슈",
    loadMoreConnections: "{count}개 더 불러오기",
    dragGroupAria: "이 연결된 그룹을 드래그하거나 이동",
    dragGroupTitle: "드래그해 연결된 그룹 이동",
    addInfluencer: "영향 주는 이슈 추가",
    addInfluenced: "영향받는 이슈 추가",
    zoomOut: "축소",
    zoomIn: "확대",
    recenter: "가운데로",
    zoomToFit: "전체 맞춤",
    zoomToContent: "콘텐츠 맞춤",
    decreaseDepth: "깊이 줄이기",
    increaseDepth: "깊이 늘리기",
    depthAria: "깊이 {depth}",
    addInfluenceTitleInfluencer: "영향 주는 이슈 추가",
    addInfluenceTitleInfluenced: "영향받는 이슈 추가",
    searchInfluencers: "영향 주는 이슈 검색",
    searchInfluenced: "영향받는 이슈 검색",
    selectIssueFirst: "먼저 이슈를 선택하세요",
    influenceAddedToast: "영향 관계를 추가했습니다",
    influenceConnectedToast: "엣지를 생성했습니다",
    influenceConnectError: "지금은 이 연결을 만들 수 없습니다.",
    influenceAlreadyVisibleToast: "이미 존재하는 영향 관계입니다. 이 그래프에 이슈를 추가했습니다.",
    influenceCreateError: "지금은 영향 관계를 만들 수 없습니다.",
    addInfluenceHintInfluencer: "\"{issueName}\"에 영향을 주는 이슈를 추가합니다.",
    addInfluenceHintInfluenced: "\"{issueName}\"의 영향을 받는 이슈를 추가합니다.",
    loadingGraph: "그래프를 불러오는 중…",
    exploreTab: "탐색",
    feedTab: "피드",
    editorTab: "편집",
    collectionCreatedToast: "그래프 컬렉션이 게시되었습니다",
    loadGraphError: "지금은 이 그래프 컬렉션을 불러올 수 없습니다.",
    positionSaveError: "최신 그래프 위치를 저장하지 못했습니다.",
    seedAttachError: "그래프는 생성되었지만 시작 이슈를 붙이지 못했습니다.",
    graphCollectionTitle: "그래프 컬렉션",
    details: "상세 정보",
    delete: "삭제",
    saveDraft: "임시저장",
    publish: "게시",
    save: "저장",
    deleteConfirmationTitle: "그래프 컬렉션을 삭제하시겠습니까?",
    deleteConfirmationBody: "이 작업은 취소할 수 없습니다. 그래프와 모든 연결이 영구적으로 삭제됩니다.",
    draftSavedToast: "임시저장이 완료되었습니다",
    publishedToast: "그래프가 게시되었습니다",
    titleRequiredForPublish: "게시하기 전에 제목을 추가하세요.",
    undo: "실행 취소",
    removeIssueCta: "이 그래프에서 제거",
    removeInfluenceCta: "이 그래프에서 영향 제거",
    removeIssueToast: "이슈를 이 그래프에서 제거했습니다",
    removeInfluenceToast: "영향을 이 그래프에서 제거했습니다",
    removeIssueError: "지금은 제거할 수 없습니다.",
    removeInfluenceError: "지금은 영향을 제거할 수 없습니다.",
    restoreSucceededToast: "복원되었습니다",
    restoreFailedToast: "복원할 수 없었습니다.",
    otherCollectionsBadge: "{count, plural, one {이 링크를 사용하는 다른 컬렉션 #개} other {이 링크를 사용하는 다른 컬렉션 #개}}",
    notesBadge: "{count, plural, one {이 엣지의 메모 #개} other {이 엣지의 메모 #개}}",
    impactsBadge: "{count, plural, one {이 링크에 연결된 영향 #개} other {이 링크에 연결된 영향 #개}}",
    solutionsBadge: "{count, plural, one {이 링크에 연결된 해결책 #개} other {이 링크에 연결된 해결책 #개}}",
    actionsBadge: "{count, plural, one {이 링크에 연결된 액션 #개} other {이 링크에 연결된 액션 #개}}",
    sourceSelfLabel: "이 그래프",
    sourceLinkedLabel: "{actor}에서",
    sourceAnonymousActor: "익명",
    remixHeader: "리믹스",
    remixSourcesSelected: "{count, plural, one {소스 1개 선택됨} other {소스 #개 선택됨}}",
    remixBridgeHint: "두 노드를 드래그하여 소스를 연결하세요.",
    remixSharedNodeHint: "{count}개 소스에서 공유됨",
    remixSourceChipAria: "소스 그래프 {title}({actor})",
    extendPanelTitle: "기존 그래프 확장",
    extendPanelSubtitle: "1개면 시작 그래프로, 2개 이상이면 리믹스 대상으로.",
    extendSearchPlaceholder: "공개 그래프 컬렉션 검색",
    extendClearSearch: "그래프 컬렉션 검색 지우기",
    extendSearchMinLength: "2자 이상 입력하세요.",
    extendNoResults: "일치하는 공개 그래프가 없습니다.",
    extendLoadMore: "더 불러오기",
    extendAddToSelection: "선택에 추가",
    extendRemoveFromSelection: "선택에서 제거",
    selectionClearedHint: "선택이 해제되었습니다",
    focusEdgeHint: "1개의 엣지에 포커스",
    neighborhoodHint: "{count, plural, one {관련 엣지 #개 표시 중} other {관련 엣지 #개 표시 중}}",
    liveRegionNeighborhood: "{count, plural, one {관련 엣지 #개를 표시 중입니다.} other {관련 엣지 #개를 표시 중입니다.}}",
    liveRegionFocus: "1개의 엣지에 포커스했습니다.",
    liveRegionCleared: "선택이 해제되었습니다.",
  },
};

const DEFAULT_LOCALE: GraphLocale = "en-US";

/**
 * Resolves the browser locale to a supported graph locale.
 */
export function resolveGraphLocale(navigatorLanguage?: string): GraphLocale {
  const raw = (navigatorLanguage || DEFAULT_LOCALE).toLowerCase();
  if (raw.startsWith("ja")) {
    return "ja-JP";
  }
  if (raw.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

/**
 * Returns localized graph UI string for the active locale.
 */
export function graphMessage(locale: GraphLocale, key: GraphCopyKey): string {
  return GRAPH_COPY[locale]?.[key] ?? GRAPH_COPY[DEFAULT_LOCALE][key];
}

/**
 * Walks a template string and rewrites every ICU-style plural expression of
 * the form `{name, plural, one {...} other {...}}` with the branch that
 * matches the given `values[name]`. Handles one level of nested braces
 * inside branch bodies via a small brace-depth counter (full recursive ICU
 * parsing is intentionally out of scope).
 */
/**
 * Read the `{name, plural,` prefix that may start at `offset` in `template`.
 * Returns the token name and the index immediately after the prefix (i.e.
 * the first character of the plural body) or `null` when the prefix does
 * not match at that position. Written without the `y` regex flag because
 * the legacy build target is ES5 and sticky regex is ES2015+.
 */
const readPluralHeader = (
  template: string,
  offset: number
): { tokenName: string; bodyStart: number } | null => {
  if (template[offset] !== "{") {
    return null;
  }
  let cursor = offset + 1;
  // Read the word token.
  const tokenStart = cursor;
  while (cursor < template.length && /\w/.test(template[cursor])) {
    cursor += 1;
  }
  if (cursor === tokenStart) {
    return null;
  }
  const tokenName = template.slice(tokenStart, cursor);
  // Read optional whitespace, a comma, optional whitespace.
  while (cursor < template.length && /\s/.test(template[cursor])) {
    cursor += 1;
  }
  if (template[cursor] !== ",") {
    return null;
  }
  cursor += 1;
  while (cursor < template.length && /\s/.test(template[cursor])) {
    cursor += 1;
  }
  // Read the literal `plural`.
  if (template.slice(cursor, cursor + 6) !== "plural") {
    return null;
  }
  cursor += 6;
  while (cursor < template.length && /\s/.test(template[cursor])) {
    cursor += 1;
  }
  if (template[cursor] !== ",") {
    return null;
  }
  cursor += 1;
  while (cursor < template.length && /\s/.test(template[cursor])) {
    cursor += 1;
  }
  return { tokenName, bodyStart: cursor };
};

const expandPluralExpressions = (
  template: string,
  values: Record<string, string | number>
): string => {
  let output = "";
  let cursor = 0;
  while (cursor < template.length) {
    const matchStart = template.indexOf("{", cursor);
    if (matchStart === -1) {
      output += template.slice(cursor);
      break;
    }
    const header = readPluralHeader(template, matchStart);
    if (!header) {
      output += template.slice(cursor, matchStart + 1);
      cursor = matchStart + 1;
      continue;
    }
    output += template.slice(cursor, matchStart);
    const { tokenName, bodyStart } = header;
    let depth = 1;
    let i = bodyStart;
    while (i < template.length && depth > 0) {
      const ch = template[i];
      if (ch === "{") {
        depth += 1;
      } else if (ch === "}") {
        depth -= 1;
        if (depth === 0) {
          break;
        }
      }
      i += 1;
    }
    if (depth !== 0) {
      output += template.slice(matchStart);
      break;
    }
    const body = template.slice(bodyStart, i);
    cursor = i + 1;

    const raw = values[tokenName];
    const count = typeof raw === "number"
      ? raw
      : Number.parseInt(String(raw ?? ""), 10);
    if (!Number.isFinite(count)) {
      output += template.slice(matchStart, cursor);
      continue;
    }

    const branches: Record<string, string> = {};
    const branchRegex = /(one|other|zero|two|few|many|=\d+)\s*\{/g;
    let branchMatch: RegExpExecArray | null;
    while ((branchMatch = branchRegex.exec(body)) !== null) {
      const branchKey = branchMatch[1];
      const branchBodyStart = branchRegex.lastIndex;
      let bDepth = 1;
      let j = branchBodyStart;
      while (j < body.length && bDepth > 0) {
        const bCh = body[j];
        if (bCh === "{") {
          bDepth += 1;
        } else if (bCh === "}") {
          bDepth -= 1;
          if (bDepth === 0) {
            break;
          }
        }
        j += 1;
      }
      if (bDepth !== 0) {
        break;
      }
      branches[branchKey] = body.slice(branchBodyStart, j);
      branchRegex.lastIndex = j + 1;
    }

    const exactKey = `=${count}`;
    let chosen = branches[exactKey];
    if (!chosen) {
      if (count === 1 && branches.one != null) {
        chosen = branches.one;
      } else if (count === 0 && branches.zero != null) {
        chosen = branches.zero;
      }
    }
    if (chosen == null) {
      chosen = branches.other ?? "";
    }
    output += chosen.replace(/#/g, String(count));
  }
  return output;
};

/**
 * ICU-plural aware token substitution. Supports two shapes:
 *   1. "Hello {name}" with simple `{token}` placeholders.
 *   2. ICU-style `{count, plural, one {# thing} other {# things}}` with a
 *      `count` value. `#` is replaced by the count inside the chosen branch.
 *
 * This is not a full ICU implementation — it covers the cases we need for
 * graphCopy without pulling in a heavyweight runtime.
 */
export function formatGraphMessage(
  locale: GraphLocale,
  key: GraphCopyKey,
  values: Record<string, string | number>
): string {
  const template = graphMessage(locale, key);
  const expanded = expandPluralExpressions(template, values);
  return Object.entries(values).reduce((message, [token, value]) => {
    return message.replace(new RegExp(`\\{${token}\\}`, "g"), String(value));
  }, expanded);
}
